import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  useFloating,
  offset,
  autoUpdate,
  flip,
  shift,
} from "@floating-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import ReactDOM from "react-dom";
import { useCompanyColors } from "@/hooks/useCompanyColors";
import { HexColorPicker, HexColorInput } from "react-colorful";
import { Button } from "@/components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeDropper } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

// Define a list of ~15 vibrant colors across the spectrum as fallback
const FALLBACK_COLORS: string[] = [
  "#ffffff", // White
  "#000000", // Black
  "#FF5733", // Red-Orange
  "#FF8D1A", // Orange
  "#FFC300", // Yellow
  "#C7EA46", // Lime Green
  "#4CAF50", // Green
  "#00BCD4", // Cyan
  "#2196F3", // Blue
  "#3F51B5", // Indigo
  "#673AB7", // Purple
  "#9C27B0", // Deep Purple
  "#E91E63", // Pink
  "#F44336", // Red
  "#795548", // Brown
  "#9E9E9E", // Grey
  "#607D8B", // Blue Grey
];

// Swatch palette colors for vibrancy
const WHEEL_COLORS = [
  "#4FD1C5", // teal
  "#38BDF8", // blue
  "#FDE68A", // yellow
  "#A5B4FC", // purple
  "#FB7185", // red
  "#F97316", // orange
  "#4FD1C5", // teal again to close the loop
];
const wheelGradient = `conic-gradient(${WHEEL_COLORS.join(", ")})`;

interface ColorPickerProps {
  onSelectColor: (colorHex: string) => void;
  currentColor?: string;
  onPopoverOpenChange?: (open: boolean) => void;
  companyId?: string;
  allowTransparency?: boolean;
  layout?: "vertical" | "horizontal";
  showHexInput?: boolean;
  children?: React.ReactNode;
  triggerClassName?: string;
}

export function ColorPicker({
  onSelectColor,
  currentColor,
  onPopoverOpenChange,
  companyId,
  allowTransparency,
  layout = "vertical",
  showHexInput = false,
  children,
  triggerClassName,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showWheel, setShowWheel] = useState(false);
  const [customColor, setCustomColor] = useState(currentColor || "#aabbcc");
  const [hexInputValue, setHexInputValue] = useState(currentColor || "");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wheelPopoverRef = useRef<HTMLDivElement>(null);
  const wheelButtonRef = useRef<HTMLButtonElement>(null);

  // Main popover positioning - positioned below the trigger with viewport awareness
  const { refs: mainRefs, floatingStyles: mainFloatingStyles } = useFloating({
    placement: "bottom-start",
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Color wheel popover positioning - positioned to the right of the wheel button
  const { refs: wheelRefs, floatingStyles: wheelFloatingStyles } = useFloating({
    placement: "right-start",
    middleware: [
      offset(12),
      flip({ fallbackPlacements: ["left-start", "bottom", "top"] }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Callback ref to combine local ref with FloatingUI ref for trigger button
  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      mainRefs.setReference(node);
    },
    [mainRefs.setReference],
  );

  // Callback ref to combine local ref with FloatingUI ref for wheel button
  const setWheelButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      wheelButtonRef.current = node;
      wheelRefs.setReference(node);
    },
    [wheelRefs.setReference],
  );

  const [isEyeDropperSupported, setIsEyeDropperSupported] = useState(false);
  const [recentColors, setRecentColors] = useState<string[]>([]);

  useEffect(() => {
    setIsEyeDropperSupported("EyeDropper" in window);

    // Load recent colors
    try {
      const stored = localStorage.getItem("ani-app-recent-colors");
      if (stored) {
        setRecentColors(JSON.parse(stored));
      }
    } catch {
      // Ignore error
    }
  }, []);

  const addToRecentColors = (color: string) => {
    if (!color || color === "transparent") return;

    setRecentColors((prev) => {
      const newColors = [color, ...prev.filter((c) => c !== color)].slice(0, 6);
      try {
        localStorage.setItem(
          "ani-app-recent-colors",
          JSON.stringify(newColors),
        );
      } catch {
        // Ignore error
      }
      return newColors;
    });
  };

  const handleEyeDropper = async () => {
    // @ts-expect-error EyeDropper is experimental and not in default Window typings
    if (!window.EyeDropper) return;

    try {
      // @ts-expect-error EyeDropper is experimental and not in default Window typings
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      handleColorSelect(result.sRGBHex);
    } catch {
      // User canceled or failed
    }
  };

  // Get company color preferences
  const { colorPalette, isLoading } = useCompanyColors();

  // Use company colors if available, otherwise fall back to default colors
  const availableColors =
    colorPalette.length > 0 ? colorPalette : FALLBACK_COLORS;

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return; // Don't add listeners if not open

    function handleOutsideEvent(event: MouseEvent | FocusEvent) {
      // Ignore if the event has been prevented
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement;

      // For focusin/focusout events, check relatedTarget (where focus is going/coming from)
      if (event.type === "focusin" && "relatedTarget" in event) {
        const relatedTarget = event.relatedTarget as HTMLElement | null;
        // If focus is moving TO an element inside the color picker, don't close
        if (relatedTarget?.closest("[data-color-picker-portal]")) {
          return;
        }
      }

      // Check if target is within ANY color picker portal (handles both main and wheel popovers)
      const isWithinAnyColorPickerPortal =
        target.closest("[data-color-picker-portal]") !== null;

      // Also check via refs as backup
      const isOnTrigger = triggerRef.current?.contains(target);
      const isWithinWheelPopover = wheelPopoverRef.current?.contains(target);

      // Check if it's an input element (for compatibility)
      const isInput = target.tagName.toLowerCase() === "input";

      // Don't close if within our components
      if (
        isWithinAnyColorPickerPortal ||
        isOnTrigger ||
        isInput ||
        isWithinWheelPopover
      ) {
        return;
      }

      setIsOpen(false);
    }

    // Handle both mouse and focus events
    document.addEventListener("mousedown", handleOutsideEvent);
    document.addEventListener("focusin", handleOutsideEvent);

    return () => {
      document.removeEventListener("mousedown", handleOutsideEvent);
      document.removeEventListener("focusin", handleOutsideEvent);
    };
  }, [isOpen]);

  // Set global flag synchronously based on isOpen state
  // This runs during render, before any effects, ensuring the flag is always current
  if (typeof window !== "undefined") {
    (window as any).__COLOR_PICKER_OPEN = isOpen;
  }

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        (window as any).__COLOR_PICKER_OPEN = false;
      }
    };
  }, []);

  // Call the callback when isOpen changes (separate effect to avoid dependency issues)
  useEffect(() => {
    if (onPopoverOpenChange) {
      onPopoverOpenChange(isOpen);
    }
  }, [isOpen, onPopoverOpenChange]);

  // Update hex input value when currentColor changes
  useEffect(() => {
    if (currentColor) {
      setHexInputValue(currentColor);
    }
  }, [currentColor]);

  // Handle click outside for color wheel popover
  useEffect(() => {
    // Also set global flag for wheel
    if (typeof window !== "undefined" && showWheel) {
      (window as any).__COLOR_PICKER_WHEEL_OPEN = true;
    }

    function handleWheelClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;

      // Don't close if clicking within any color picker portal or the wheel button
      const isWithinAnyPortal =
        target.closest("[data-color-picker-portal]") !== null;
      const isWheelButton = wheelButtonRef.current?.contains(target);

      if (isWithinAnyPortal || isWheelButton) {
        return;
      }

      setShowWheel(false);
    }
    if (showWheel) {
      document.addEventListener("mousedown", handleWheelClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleWheelClickOutside);
      if (typeof window !== "undefined") {
        (window as any).__COLOR_PICKER_WHEEL_OPEN = false;
      }
    };
  }, [showWheel]);

  const handleColorSelect = (color: string) => {
    onSelectColor(color);
    addToRecentColors(color);
    setIsOpen(false);
    setShowWheel(false);
  };

  const handleHexInputChange = (value: string) => {
    setHexInputValue(value);
  };

  const handleHexInputBlur = () => {
    // Apply color when input loses focus
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexRegex.test(hexInputValue)) {
      handleColorSelect(hexInputValue);
    }
  };

  const handleHexInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Apply color when Enter is pressed
      const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      if (hexRegex.test(hexInputValue)) {
        handleColorSelect(hexInputValue);
      }
    }
  };

  return (
    <>
      <button
        ref={setTriggerRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const newIsOpen = !isOpen;
          // Set flag synchronously BEFORE state update
          if (typeof window !== "undefined") {
            (window as any).__COLOR_PICKER_OPEN = newIsOpen;
          }
          setIsOpen(newIsOpen);
        }}
        className={`flex cursor-pointer items-center justify-center transition-all duration-150 ease-in-out disabled:cursor-not-allowed ${
          isOpen ? "" : triggerClassName ? "" : ""
        } ${!children ? "w-6 h-6 flex-shrink-0 rounded-full border-2 border-slate-300 dark:border-slate-500" : ""} ${
          triggerClassName ? triggerClassName : ""
        }`}
        style={{
          background:
            !children && currentColor && currentColor !== "transparent"
              ? currentColor
              : !children && currentColor === "transparent"
                ? "#ffffff"
                : !children
                  ? "conic-gradient(#4FD1C5, #38BDF8, #FDE68A, #A5B4FC, #FB7185, #F97316, #4FD1C5)"
                  : undefined,
        }}
        title={currentColor || "Select a color"}
        disabled={isLoading}
      >
        {children ? (
          children
        ) : currentColor === "transparent" ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* <circle
              cx="12"
              cy="12"
              r="10"
              fill="white"
              stroke="#E2E8F0"
              strokeWidth="1.5"
            /> */}
            <path
              d="M5.63604 18.364L18.364 5.63603"
              stroke="#EF4444"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : null}
      </button>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={mainRefs.setFloating}
            style={mainFloatingStyles}
            data-color-picker-portal
            className={`z-[999999] bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-3 ${layout === "horizontal" ? "max-w-[200px]" : ""}`}
            onMouseDown={(e) => {
              // Allow inputs to receive focus
              if ((e.target as HTMLElement).tagName === "INPUT") {
                e.stopPropagation();
                return;
              }
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {recentColors.length > 0 && (
                <div className="mb-2 pb-2 ">
                  <div className="text-[9px] text-slate-500 dark:text-slate-400 uppercase font-mono tracking-widest mb-1.5 ml-1">
                    Recent
                  </div>
                  <div
                    className={
                      layout === "horizontal"
                        ? "grid grid-cols-6 gap-2 justify-items-center"
                        : "flex flex-wrap gap-2 justify-center"
                    }
                  >
                    {recentColors.map((color) => (
                      <button
                        key={color}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleColorSelect(color);
                        }}
                        className="w-6 h-6 rounded-full flex items-center justify-center text-slate-900 dark:text-slate-100 transition-all duration-150 ease-in-out border-white dark:border-slate-700 "
                        style={{ backgroundColor: color }}
                        title={color}
                      >
                        {currentColor === color && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className={`w-5 h-5 ${currentColor === "#ffffff" ? "text-slate-900" : "text-white"}`}
                            style={{ mixBlendMode: "hard-light" }}
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[9px] text-slate-500 dark:text-slate-400 uppercase font-mono tracking-widest mb-1.5 ml-1">
                Colours
              </div>
              <div
                className={
                  layout === "horizontal"
                    ? "grid grid-cols-6 gap-2 justify-items-center"
                    : "flex flex-col gap-2 items-center"
                }
              >
                {allowTransparency && (
                  <button
                    key="transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColorSelect("transparent");
                    }}
                    className="w-6 h-6 rounded-full flex items-center justify-center bg-white transition-all duration-150 ease-in-out border border-slate-200 dark:border-slate-700 "
                    title="Transparent"
                  >
                    {currentColor === "transparent" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-5 h-5 text-blue-600 "
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="12" cy="12" r="10" fill="white" />
                        <path
                          d="M5.63604 18.364L18.364 5.63603"
                          stroke="#EF4444"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </button>
                )}
                {/* Black */}
                <button
                  key="black"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleColorSelect("#000000");
                  }}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white transition-all duration-150 ease-in-out "
                  style={{ backgroundColor: "#000000" }}
                  title="Black"
                >
                  {currentColor === "#000000" && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-5 h-5 text-white"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                {/* White */}
                <button
                  key="white"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleColorSelect("#ffffff");
                  }}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-slate-900 transition-all duration-150 ease-in-out border border-slate-200 dark:border-slate-700"
                  style={{ backgroundColor: "#ffffff" }}
                  title="White"
                >
                  {currentColor === "#ffffff" && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-5 h-5 text-slate-900 "
                      style={{ mixBlendMode: "hard-light" }}
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                {availableColors
                  .filter((c) => c !== "#000000" && c !== "#ffffff")
                  .map((color) => (
                    <button
                      key={color}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleColorSelect(color);
                      }}
                      className="w-6 h-6 rounded-full flex items-center justify-center text-slate-900 dark:text-slate-100 transition-all duration-150 ease-in-out"
                      style={{ backgroundColor: color }}
                      title={color}
                    >
                      {currentColor === color && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-5 h-5"
                          style={{ mixBlendMode: "hard-light" }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                {/* Color wheel button */}
                <button
                  ref={setWheelButtonRef}
                  className="w-7 h-7 flex items-center justify-center rounded-full border border-slate-300 dark:border-slate-600 hover:scale-110 transition"
                  title="Custom color wheel"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWheel((v) => !v);
                  }}
                  style={{
                    outline: showWheel ? "2px solid #888" : undefined,
                    background: wheelGradient,
                  }}
                >
                  <span
                    className=" rounded-full block"
                    style={{ background: wheelGradient }}
                  />
                </button>
              </div>

              {/* Hex input field */}
              {showHexInput && (
                <div className="mt-3 pt-1 ">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={hexInputValue}
                      onChange={(e) => handleHexInputChange(e.target.value)}
                      onBlur={handleHexInputBlur}
                      onKeyDown={handleHexInputKeyDown}
                      onMouseDown={(e) => {
                        // Stop propagation to prevent any parent handlers
                        e.stopPropagation();
                        // Set flag immediately on mousedown (before blur fires on editor)
                        if (typeof window !== "undefined") {
                          (window as any).__COLOR_PICKER_OPEN = true;
                          (window as any).__COLOR_PICKER_INPUT_FOCUSED = true;
                        }
                      }}
                      onClick={(e) => {
                        // Stop propagation
                        e.stopPropagation();
                        // Ensure input is focused and cursor is at the end
                        const input = e.currentTarget;
                        setTimeout(() => {
                          input.focus();
                          input.setSelectionRange(
                            input.value.length,
                            input.value.length,
                          );
                        }, 0);
                      }}
                      onFocus={(e) => {
                        // Allow focus to bubble so parent toolbars know we are interacting
                      }}
                      placeholder="#000000"
                      className="block w-full rounded-lg bg-white px-3 py-1.5 text-base text-slate-900 dark:bg-slate-700/90 dark:text-slate-200 outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-700 focus:ring-indigo-400 dark:focus:ring-indigo-400"
                      maxLength={7}
                      style={{
                        colorScheme: "dark",
                      }}
                    />
                    {/* EyeDropper button */}
                    {isEyeDropperSupported && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEyeDropper();
                        }}
                        className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full  hover:bg-slate-50 dark:hover:bg-slate-700 transition text-slate-500 dark:text-slate-400"
                        title="Pick color from screen"
                      >
                        <FontAwesomeIcon
                          icon={faEyeDropper}
                          className="w-3.5 h-3.5"
                        />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>,
          document.body,
        )}
      {/* Colorful color wheel picker in its own popover (always render at root when showWheel) */}
      {showWheel &&
        ReactDOM.createPortal(
          <div
            data-color-picker-portal
            ref={(node) => {
              wheelRefs.setFloating(node);
              wheelPopoverRef.current = node;
            }}
            className="z-[9999999] bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4 flex flex-col items-center"
            style={wheelFloatingStyles}
            onMouseDown={(e) => {
              // Stop propagation to prevent toolbar from refocusing editor
              e.stopPropagation();
            }}
          >
            <HexColorPicker
              color={customColor}
              onChange={setCustomColor}
              style={{ width: 160, height: 120 }}
            />
            <div className="flex flex-col items-center gap-3 mt-4 w-full">
              <div
                className="relative w-full"
                onMouseDown={(e) => {
                  // Stop propagation so toolbar doesn't refocus editor
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-semibold">
                  #
                </span>
                <HexColorInput
                  color={customColor}
                  onChange={setCustomColor}
                  className="w-full text-center tracking-widest font-mono rounded-md bg-slate-100 dark:bg-slate-700 dark:text-white text-sm font-semibold border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 py-2"
                />
              </div>
              <Button
                color="indigo"
                className="w-full"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleColorSelect(customColor);
                }}
              >
                Choose
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
